import { Icon, Popover, useDisclosure } from '@chakra-ui/react';
import { forwardRef, useId, useImperativeHandle, useState } from 'react';
import FocusLock from 'react-focus-lock';
import { MdClose, MdFilterAlt, MdFilterList } from 'react-icons/md';
import { useAuth } from '../../../../services/auth0';
import MButton from '../../MButton';
import MCustomIconButton from '../../MCustomIconButton';
import { MCustomSelect } from '../../MCustomSelect/MCustomSelect';
import { MainSearchInputV2 } from '../../MCustomSelect/components/MainSearchInputV2';
import { UserAvatarAndName } from '../../MCustomSelect/components/UserAvatarAndName';
import { FOCUS_WITHOUT_SCROLLING } from '../../Overlays';
import { MFlex } from '../../chakra';
import {
  DataTableFilterRef,
  FilterProps,
  OwnerFilterData,
} from '../DataTableTypes';
import { getColumnFilterKey } from '../tableUtilsNew';

export const DataTableHeaderOwnerFilter = forwardRef<
  DataTableFilterRef,
  FilterProps<any>
>(({ column, table }, ref) => {
  const key = getColumnFilterKey(column);
  const { tenantId } = useAuth();
  const { open, onOpen, onClose } = useDisclosure();

  const getInitialState = (): OwnerFilterData | null => {
    const existingFilter = table.options.meta?.selectedFilters?.get(key);
    if (
      existingFilter &&
      typeof existingFilter === 'object' &&
      'ownerId' in existingFilter
    ) {
      return existingFilter as OwnerFilterData;
    }
    return null;
  };

  const [appliedState, setAppliedState] = useState<OwnerFilterData | null>(
    getInitialState,
  );
  const [selectedState, setSelectedState] = useState<OwnerFilterData | null>(
    getInitialState,
  );

  useImperativeHandle(ref, (): DataTableFilterRef => ({
    resetFilters: () => {
      handleResetFilters();
    },
    setFilterValue: ({ columnKey, value }) => {
      if (columnKey === column.id) {
        column.setFilterValue(value);
      }
    },
  }));

  const handleResetFilters = () => {
    setSelectedState(null);
    setAppliedState(null);
    column.setFilterValue(undefined);

    if (table.options.meta?.onFilterUpdate) {
      table.options.meta.onFilterUpdate(key, undefined);
    }

    onClose();
  };

  const handleApplyFilters = () => {
    setAppliedState(selectedState);

    if (selectedState) {
      column.setFilterValue(selectedState);
      if (table.options.meta?.onFilterUpdate) {
        table.options.meta.onFilterUpdate(key, selectedState);
      }
    } else {
      column.setFilterValue(undefined);
      if (table.options.meta?.onFilterUpdate) {
        table.options.meta.onFilterUpdate(key, undefined);
      }
    }

    onClose();
  };

  const handleClose = () => {
    // Revert to last applied state when closing without applying
    setSelectedState(appliedState);
    onClose();
  };

  const filtersApplied = !!appliedState?.ownerId;

  const hasChanges =
    selectedState?.ownerId !== (appliedState?.ownerId ?? undefined);

  const titleId = useId();

  return (
    <Popover.Root
      open={open}
      lazyMount
      unmountOnExit
      onOpenChange={(e) => {
        if (e.open) {
        } else {
          handleClose();
        }
      }}
      positioning={{
        placement: 'bottom-start',
        offset: { mainAxis: 0, crossAxis: 0 },
        strategy: 'fixed',
      }}
    >
      <Popover.Trigger asChild>
        <MCustomIconButton
          data-testid={`table-column-filter-${key}`}
          btnSize={5}
          onClick={onOpen}
          icon={filtersApplied ? MdFilterAlt : MdFilterList}
          aria-label="Filter"
          variant="icon"
          ml="2"
          color={filtersApplied ? 'tIndigo.base' : 'tPurple.base'}
          iconColor={filtersApplied ? 'tIndigo.base' : 'tPurple.base'}
        />
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content aria-labelledby={titleId}>
          {open && (
            <FocusLock focusOptions={FOCUS_WITHOUT_SCROLLING}>
              <Popover.Arrow>
                <Popover.ArrowTip />
              </Popover.Arrow>
              <Popover.CloseTrigger aria-label="Close">
                <Icon asChild>
                  <MdClose />
                </Icon>
              </Popover.CloseTrigger>
              <Popover.Header id={titleId}>Filter by Owner</Popover.Header>
              <Popover.Body minW="304px">
                <MCustomSelect
                  clearable
                  useMainInputAsSearchInput
                  returnItem
                  itemTitle="name"
                  itemValue="id"
                  displayAvatar
                  endpoint={`/api/tenants/${tenantId}/users`}
                  additionalSearchParams={{ sort: 'name' }}
                  loadAll
                  renderItemContent={({ title }) => (
                    <UserAvatarAndName
                      userName={title}
                      textProps={{ fontSize: 'sm' }}
                    />
                  )}
                  MainInputComponent={MainSearchInputV2}
                  value={
                    selectedState
                      ? {
                          id: selectedState.ownerId,
                          name: selectedState.ownerName,
                        }
                      : null
                  }
                  onChange={(item: any) => {
                    if (item && item.id) {
                      setSelectedState({
                        ownerId: item.id,
                        ownerName: item.name ?? item.id,
                      });
                    } else {
                      setSelectedState(null);
                    }
                  }}
                />
              </Popover.Body>
              <Popover.Footer>
                <MFlex justifyContent="space-between">
                  <MButton
                    variant="tertiary"
                    size="sm"
                    onClick={handleResetFilters}
                    isDisabled={!hasChanges && !filtersApplied}
                  >
                    Reset
                  </MButton>
                  <MButton size="sm" onClick={handleApplyFilters}>
                    Apply
                  </MButton>
                </MFlex>
              </Popover.Footer>
            </FocusLock>
          )}
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
});
